import { useEffect, useRef, useState } from 'react'
import { IconVideo } from '@tabler/icons-react'
import { useAuthStore } from '../../store/useAuthStore'
import { useLearningStore } from '../../store/useLearningStore'
import { useWrongQuizStore } from '../../store/useWrongQuizStore'
import { notifications } from '@mantine/notifications'
import styles from '../../styles/user/LearningContainer.module.scss'

const LearningProgressTabs = ({
  showVideo = true,
  userType = 'student',
  quizDirectList,
}) => {
  const user = useAuthStore((state) => state.user)

  const isGeneralUser = userType !== 'student'
  const isQuizOnlyStep = isGeneralUser
    ? false
    : user?.STEP
      ? user.STEP % 5 === 0
      : false

  const listRef = useRef(null)
  const itemRefs = useRef([])

  const [localAnswered, setLocalAnswered] = useState(new Set())

  const {
    questions,
    currentStep,
    currentQuestionIndex,
    setCurrentStep,
    setCurrentQuestionIndex,
    videoCompleted,
    userAnswers,
  } = useLearningStore()

  useEffect(() => {
    if (quizDirectList?.data?.length > 0) {
      setTimeout(() => {
        const answered = new Set(quizDirectList.data.map((q) => q.QUESTION_ID))
        setLocalAnswered(answered)
        useLearningStore.setState({ videoCompleted: true })
      }, 500)
    }
  }, [quizDirectList])

  useEffect(() => {
    if (userAnswers.length > 0) {
      setLocalAnswered((prev) => {
        const newSet = new Set(prev)
        userAnswers.forEach((answer) => {
          newSet.add(answer.questionId)
        })
        console.log('✅ 로컬 답변 업데이트:', newSet)
        return newSet
      })
    }
  }, [userAnswers])

  const {
    wrongQuestions,
    currentQuestionIndex: wrongCurrentIndex,
    setCurrentQuestionIndex: setWrongCurrentIndex,
    userAnswers: wrongUserAnswers,
  } = useWrongQuizStore()

  const displayQuestions = isQuizOnlyStep ? wrongQuestions : questions
  const displayCurrentIndex = isQuizOnlyStep ? wrongCurrentIndex : currentQuestionIndex
  const actualShowVideo = isGeneralUser ? false : showVideo

  const isQuestionAnswered = (questionIndex) => {
    const question = displayQuestions[questionIndex]
    if (!question) return false
    return userAnswers.some((answer) => answer.questionId === question.QUESTION_ID)
  }

  const getQuestionStatus = (questionId, index) => {
    const isActive = displayCurrentIndex === index
    const answered = isQuizOnlyStep
      ? wrongUserAnswers.find((answer) => answer.questionId === questionId)
      : userAnswers.find((answer) => answer.questionId === questionId)
    if (isActive) return 'active'
    if (!answered) return 'unanswered'
    return answered.isCorrect ? 'correct' : 'wrong'
  }

  const handleVideoTabClick = () => {
    if (actualShowVideo && !isQuizOnlyStep) {
      setCurrentStep('video')
    }
  }

  const handleQuestionTabClick = (questionIndex) => {
    console.log('actualShowVideo ', actualShowVideo)
    console.log('videoCompleted ', videoCompleted)
    console.log('isQuizOnlyStep ', isQuizOnlyStep)

    if (actualShowVideo && !videoCompleted && !isQuizOnlyStep) {
      notifications.show({
        title: 'Alert',
        message: 'Please watch the video first.',
        color: 'orange',
      })
      return
    }

    if (isQuizOnlyStep) {
      setWrongCurrentIndex(questionIndex)
    } else {
      setCurrentStep('quiz')
      setCurrentQuestionIndex(questionIndex)
    }
  }

  // 활성 항목 자동 스크롤
  useEffect(() => {
    const tabIndex = actualShowVideo && !isQuizOnlyStep
      ? displayCurrentIndex + 1
      : displayCurrentIndex
    const activeEl = itemRefs.current[tabIndex]
    if (activeEl && listRef.current) {
      activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [currentStep, displayCurrentIndex, isQuizOnlyStep, actualShowVideo])

  // 진행률 계산
  const answeredCount = isQuizOnlyStep ? wrongUserAnswers.length : userAnswers.length
  const totalCount = displayQuestions.length
  const progressPct = totalCount > 0 ? Math.round((answeredCount / totalCount) * 100) : 0
  const levelId = user?.LEVEL_ID || ''

  // 활동 라벨
  const activityLabel = isQuizOnlyStep
    ? 'Wrong Answer Activity'
    : isGeneralUser
      ? 'Level Test'
      : 'Solving Questions'

  const detailUnit = (questions[0]?.DETAIL_UNIT || '').replace(/\s*\(\d+\)\s*$/, '')
  const lessonTitle = isQuizOnlyStep
    ? 'Review Mistakes'
    : isGeneralUser
      ? 'Level Test'
      : detailUnit || `Lesson ${levelId}`

  return (
    <div className={styles.sidebarInner}>

      {/* 헤더: 오답 모드면 amber, 일반 모드면 dark blue */}
      <div className={`${styles.sidebarHeader} ${isQuizOnlyStep ? styles.sidebarHeaderWrong : ''}`}>
        {isQuizOnlyStep && (
          <div className={styles.sidebarWrongActivityLabel}>Wrong Answer Activity</div>
        )}
        <div className={styles.sidebarLessonTitle}>{lessonTitle}</div>
      </div>

      {/* Watch 버튼 — 비디오 모드일 때만 */}
      {actualShowVideo && !isQuizOnlyStep && (
        <button
          className={`${styles.watchBtn} ${currentStep === 'video' ? styles.watchBtnActive : ''}`}
          onClick={handleVideoTabClick}
        >
          <span className={styles.watchBtnIcon}>
            <IconVideo size={22} stroke={2} />
          </span>
          <span className={styles.watchBtnContent}>
            <span className={styles.watchBtnTitle}>
              {videoCompleted ? 'Watch Again' : 'Watch Lesson'}
            </span>
            <span className={styles.watchBtnSub}>
              {currentStep === 'video' ? 'Now watching...' : 'Video Lesson'}
            </span>
          </span>
        </button>
      )}

      {/* 진행률 */}
      <div className={`${styles.sidebarProgress} ${isQuizOnlyStep ? styles.sidebarProgressWrong : ''}`}>
        <div className={styles.sidebarProgressTop}>
          <span className={styles.sidebarProgressLabel}>
            {isQuizOnlyStep ? 'Mistakes Fixed' : 'Progress'}
          </span>
          <span className={styles.sidebarProgressCount}>{answeredCount}/{totalCount}</span>
        </div>
        <div className={styles.sidebarProgressBar}>
          <div
            className={`${styles.sidebarProgressFill} ${isQuizOnlyStep ? styles.sidebarProgressFillWrong : ''}`}
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <div className={styles.sidebarProgressPct}>
          {isQuizOnlyStep ? `${progressPct}% Fixed` : `${progressPct}% Answered`}
        </div>
      </div>

      <div className={styles.sidebarDivider} />
      <div className={styles.sidebarQuestionsLabel}>
        {isQuizOnlyStep ? 'Review Questions' : 'Questions'}
      </div>

      {/* 문제 목록 */}
      <div className={styles.sidebarQuestionList} ref={listRef}>
        {displayQuestions.map((q, index) => {
          const questionId = q.QUESTION_ID
          const status = getQuestionStatus(questionId, index)
          const isActive = status === 'active'
          const isVideoDisabled = !videoCompleted && actualShowVideo && !isQuizOnlyStep
          const tabIndex = actualShowVideo && !isQuizOnlyStep ? index + 1 : index

          const itemClass = [
            styles.sidebarItem,
            isActive ? styles.sidebarItemActive : '',
            status === 'correct' ? styles.sidebarItemCorrect : '',
            status === 'wrong' ? styles.sidebarItemWrong : '',
          ].filter(Boolean).join(' ')

          return (
            <div
              key={index}
              ref={(el) => { itemRefs.current[tabIndex] = el }}
              className={itemClass}
              onClick={() => !isVideoDisabled && handleQuestionTabClick(index)}
              style={{ opacity: isVideoDisabled ? 0.45 : 1, cursor: isVideoDisabled ? 'default' : 'pointer' }}
            >
              <span className={styles.sidebarItemCircle}>
                {index + 1}
              </span>
              <span className={styles.sidebarItemLabel}>Question {index + 1}</span>
              {status === 'correct' && (
                <span className={`${styles.sidebarItemStatus} ${styles.statusCorrect}`}>✓</span>
              )}
              {status === 'wrong' && (
                <span className={`${styles.sidebarItemStatus} ${styles.statusWrong}`}>✗</span>
              )}
            </div>
          )
        })}
      </div>

      {/* 하단 메도우 일러스트 */}
      <img
        src="/assets/sidebar-meadow.png"
        alt=""
        className={styles.sidebarMeadow}
      />
    </div>
  )
}

export default LearningProgressTabs
